import { makeObservable, observable } from 'mobx';
import { BidderCampaignCrudStore } from './bidder-campaign-crud.store';
import { HttpStore } from '@monorepo/controlled/src/stores/http.store';
import { BidderApi } from '../../apis/bidder.api';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { BidderInputOptionsStore } from './bidder-input-options.store';
import { BidderWhitelistBlacklistStore } from './bidder-whitelist-blacklist.store';
import { BaseListStore } from '@monorepo/controlled/src/stores/base-list.store';
import { BidderCampaignTableModel, IBidderAdvertiser } from '../../models/bidder-campaign/bidder-camapign.model';
import { ApiChangeLogModel } from '../../models/bidder-logs.model';
import { bidderCampaignFilterPrototypes, filterPrototypesByProp } from '../../components/filters-menu/filters';
import { FiltersMap, FilterStore } from '@monorepo/controlled/src/stores/filter.store';
import { FilterModel } from '@monorepo/controlled/src/models/filter.model';
import { Statuses } from '../../enums/status';
import { StringActions } from '@monorepo/tools/src/lib/utils/string';
import { TableStore } from '@monorepo/controlled/src/stores/table.store';
import { SettingsStore } from '@monorepo/controlled/src/stores/settings.store';
import { BidderCampaignGroup } from '../../models/bidder-campaign/bidder-camapign.model';
import { BidderModelTableModel } from '../../models/bidder-model.model';
import { TrafficEntityModel } from '../../models/traffic-entity.model';

export class BidderStore {
	crud = new BidderCampaignCrudStore();
	inputOptions = new BidderInputOptionsStore();
	whitelistBlacklist = new BidderWhitelistBlacklistStore();

	campaignsList = new BaseListStore<BidderCampaignTableModel>({
		// response is { campaigns: [...] } instead of [...], handled in BaseListStore via listPath
		listFunc: BidderApi.getCampaigns as any,
		listPath: 'campaigns',
		model: BidderCampaignTableModel,
	});

	publishersList = new HttpStore<undefined, { success: boolean; publishers: { id: id; name: string }[] }>({
		httpFunc: BidderApi.getPublishers,
	});

	advertisers = new HttpStore<undefined, { success: boolean; advertisers: IBidderAdvertiser[] }>({
		httpFunc: BidderApi.getAdvertisers,
	});

	campaignGroups = new HttpStore<id, { success: boolean; campaign_groups: BidderCampaignGroup[] }>({
		httpFunc: id => BidderApi.getCampaignGroups(id),
	});

	private _stateMachineIds?: HttpStore<undefined, { state_machine_ids: id[]; status: string }>;

	get stateMachineIds(): HttpStore<undefined, { state_machine_ids: id[]; status: string }> {
		if (!this._stateMachineIds) {
			this._stateMachineIds = new HttpStore({ httpFunc: BidderApi.getStateMachineIds });
		}
		return this._stateMachineIds;
	}

	changesLogsList = new BaseListStore<ApiChangeLogModel>({
		listFunc: BidderApi.getApiChangesLogDataWithParams as any,
		listPath: 'api_changes_log_data',
		model: ApiChangeLogModel,
	});

	modelsList = new BaseListStore<BidderModelTableModel>({
		listFunc: BidderApi.getModels as any,
		listPath: 'models',
		model: BidderModelTableModel,
	});

	trafficList = new BaseListStore<TrafficEntityModel>({
		listFunc: BidderApi.getTrafficEntities as any,
		listPath: 'traffic_entities',
		model: TrafficEntityModel,
	});

	settingsStore: SettingsStore;

	constructor(settingsStore: SettingsStore) {
		makeObservable(this, {
			crud: observable,
		});
		this.settingsStore = settingsStore;
		this.createDefaultFilters();
	}

	createDefaultFilters() {
		const prototype = bidderCampaignFilterPrototypes.find(filter => filter.MenuComponent === 'Status');
		let defaultFilters: FiltersMap = new Map();

		if (prototype) {
			defaultFilters = new Map([
				[
					1,
					new FilterModel({
						index: 1,
						label: `Status is ${Statuses.Active}`,
						action: StringActions.Equals,
						value: [Statuses.Active],
						prototype,
					}),
				],
			]);
		}

		const key = 'biddercampaigns';

		if (!this.settingsStore.getPages().get(key)) {
			this.settingsStore.getPages().set(key, { tableStore: new TableStore() });
		}
		const bidderCampaignFilterByKey = new FilterStore({
			key,
			currentFilters: defaultFilters,
			filterPrototypesByProp,
		});
		this.settingsStore.getPages().get(key)?.tableStore.setFiltersStore(key, bidderCampaignFilterByKey);

		// Setup filters for bidder changes logs
		const bidderChangesLogsKey = 'bidderchangeslogs';
		if (!this.settingsStore.getPages().get(bidderChangesLogsKey)) {
			this.settingsStore.getPages().set(bidderChangesLogsKey, { tableStore: new TableStore() });
		}
		const bidderChangesLogsFilterByKey = new FilterStore({
			key: bidderChangesLogsKey,
			currentFilters: new Map(),
			filterPrototypesByProp,
		});
		this.settingsStore
			.getPages()
			.get(bidderChangesLogsKey)
			?.tableStore.setFiltersStore(bidderChangesLogsKey, bidderChangesLogsFilterByKey);

		const bidderModelsKey = 'biddermodels';
		if (!this.settingsStore.getPages().get(bidderModelsKey)) {
			this.settingsStore.getPages().set(bidderModelsKey, { tableStore: new TableStore() });
		}
		const bidderModelsFilterByKey = new FilterStore({
			key: bidderModelsKey,
			currentFilters: new Map(),
			filterPrototypesByProp,
		});
		this.settingsStore.getPages().get(bidderModelsKey)?.tableStore.setFiltersStore(bidderModelsKey, bidderModelsFilterByKey);

		const bidderTrafficKey = 'biddertraffic';
		if (!this.settingsStore.getPages().get(bidderTrafficKey)) {
			this.settingsStore.getPages().set(bidderTrafficKey, { tableStore: new TableStore() });
		}
		const bidderTrafficFilterByKey = new FilterStore({
			key: bidderTrafficKey,
			currentFilters: new Map(),
			filterPrototypesByProp,
		});
		this.settingsStore.getPages().get(bidderTrafficKey)?.tableStore.setFiltersStore(bidderTrafficKey, bidderTrafficFilterByKey);
	}

	getCrud() {
		return this.crud;
	}

	getInputOptions() {
		return this.inputOptions;
	}

	getActiveAdvertisers() {
		return this.advertisers.getData()?.advertisers.filter(a => a.status === 'ACTIVE') || [];
	}

	getCampaignGroups() {
		return this.campaignGroups.getData()?.campaign_groups || [];
	}

	getStateMachineIds() {
		return this.stateMachineIds.getData()?.state_machine_ids ?? [];
	}
}
